/**
 * Sync the document direction with the active language after hydration, and
 * migrate any legacy localStorage preference into the cookie that now drives
 * SSR (so the choice survives and server/client stay in sync).
 *
 * On storefront routes, the storefront-locale plugin owns dir/lang — do not
 * overwrite it with the app cookie here.
 */
import {
  applyLanguageToDocument,
  isKawadernaLang,
  normalizeKawadernaLang,
  useHrLangCookie,
} from '~/composables/useKawadernaI18n'
import { isStorefrontRequest } from '~/composables/useStorefrontHost'
import { useStorefrontLangOverride } from '~/composables/storefront/langState'
import {
  readPublicLegalLangFromLocation,
  usePublicLegalLangLock,
} from '~/composables/usePublicLegalLocale'

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.hook('app:mounted', () => {
    const { lang, setLanguage } = useKawadernaI18n()
    const override = useStorefrontLangOverride()
    const onStorefront = isStorefrontRequest(window.location.pathname)
    const legalLock = usePublicLegalLangLock().value
      ?? readPublicLegalLangFromLocation()

    const legacy = localStorage.getItem('hr-lang')
    const cookie = useHrLangCookie()

    // Public legal pages honor /en/* and ?lang= over the saved cookie.
    if (legalLock) {
      if (lang.value !== legalLock) void setLanguage(legalLock)
      applyLanguageToDocument(legalLock)
      return
    }

    // First load after the cookie migration: adopt the old localStorage value.
    // Skip on the storefront — flipping the app language here fights the store
    // language already painted into the HTML.
    if (!onStorefront) {
      if (isKawadernaLang(legacy) && cookie.value !== legacy) {
        void setLanguage(legacy)
      }
      else if (isKawadernaLang(cookie.value) && lang.value !== cookie.value) {
        void setLanguage(normalizeKawadernaLang(cookie.value))
      }
    }

    if (isKawadernaLang(override.value)) {
      applyLanguageToDocument(override.value)
      return
    }

    applyLanguageToDocument(lang.value)
  })
})
